class CollectionsEnhancedSlider {
  constructor(sliderElement) {
    this.slider = sliderElement;
    this.track = this.slider.querySelector('[data-slider-track]');
    this.slides = this.slider.querySelectorAll('[data-slider-slide]');
    this.buttonsContainer = this.slider.querySelector('[data-slider-buttons]');
    this.prevButton = this.slider.querySelector('[data-slider-button-prev]');
    this.nextButton = this.slider.querySelector('[data-slider-button-next]');
    this.paginationContainer = this.slider.querySelector('[data-slider-pagination]');
    this.paginationDots = this.slider.querySelectorAll('[data-slider-pagination-dot]');
    
    // Detectar si es slider de tablet (múltiples items por página)
    this.breakpoint = this.slider.dataset.breakpoint || 'mobile';
    this.itemsPerPage = parseInt(this.slider.dataset.itemsPerPage) || 1;
    this.isTabletSlider = this.breakpoint === 'tablet' && this.itemsPerPage > 1;
    
    this.currentPage = 0;
    this.totalSlides = this.slides.length;
    this.totalPages = this.isTabletSlider ? Math.ceil(this.totalSlides / this.itemsPerPage) : this.totalSlides;
    this.autoplayInterval = null;
    
    // Get settings from data attributes
    this.settings = {
      showArrowsMobile: this.slider.dataset.showArrowsMobile === 'true',
      showDotsMobile: this.slider.dataset.showDotsMobile === 'true',
      enableSwipeMobile: this.slider.dataset.enableSwipeMobile === 'true',
      showArrowsDesktop: this.slider.dataset.showArrowsDesktop === 'true',
      showDotsDesktop: this.slider.dataset.showDotsDesktop === 'true',
      enableAutoplay: this.slider.dataset.enableAutoplay === 'true',
      autoplaySpeed: parseInt(this.slider.dataset.autoplaySpeed) * 1000 || 5000
    };
    
    this.init();
  }
  
  init() {
    if (this.totalSlides <= 1 && !this.isTabletSlider) {
      this.hideNavigation();
      return;
    }
    
    // Para tablets con menos de itemsPerPage elementos, no mostrar navegación pero sí el contenido
    if (this.isTabletSlider && this.totalSlides <= this.itemsPerPage) {
      this.hideNavigation();
      return;
    }
    
    this.applySettings();
    this.setupEventListeners();
    this.updateSlider();
    
    if (this.settings.enableAutoplay) {
      this.startAutoplay();
    }
    
    // Handle window resize to update visibility
    window.addEventListener('resize', () => this.applySettings());
  }
  
  applySettings() {
    const isMobile = window.innerWidth <= 768;
    const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
    
    // Show/hide arrows based on device
    if (this.buttonsContainer) {
      if (this.isTabletSlider && isTablet) {
        // En tablets con slider multi-item, siempre mostrar flechas
        this.buttonsContainer.style.display = 'flex';
      } else if (isMobile) {
        this.buttonsContainer.style.display = this.settings.showArrowsMobile ? 'flex' : 'none';
      } else {
        this.buttonsContainer.style.display = this.settings.showArrowsDesktop ? 'flex' : 'none';
      }
    }
    
    // Show/hide pagination dots based on device
    if (this.paginationContainer) {
      if (this.isTabletSlider && isTablet) {
        // En tablets con slider multi-item, siempre mostrar dots
        this.paginationContainer.style.display = 'flex';
      } else if (isMobile) {
        this.paginationContainer.style.display = this.settings.showDotsMobile ? 'flex' : 'none';
      } else {
        this.paginationContainer.style.display = this.settings.showDotsDesktop ? 'flex' : 'none';
      }
    }
  }
  
  setupEventListeners() {
    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.prevSlide();
        this.resetAutoplay();
      });
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.nextSlide();
        this.resetAutoplay();
      });
    }
    
    // Pagination dots
    this.paginationDots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
        this.resetAutoplay();
      });
    });
    
    // Touch/swipe support (only if enabled)
    const isMobile = window.innerWidth <= 768;
    if ((isMobile && this.settings.enableSwipeMobile) || !isMobile) {
      this.addTouchSupport();
    }
    
    // Keyboard navigation
    this.slider.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
        this.resetAutoplay();
      }
      if (e.key === 'ArrowRight') {
        this.nextSlide();
        this.resetAutoplay();
      }
    });
  }
  
  addTouchSupport() {
    let startX = 0;
    let startY = 0;
    let isDragging = false;
    
    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
    }, { passive: true });
    
    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = Math.abs(startX - currentX);
      const diffY = Math.abs(startY - currentY);
      
      // Solo prevenir el scroll si el movimiento es más horizontal que vertical
      if (diffX > diffY && diffX > 10) {
        e.preventDefault();
      }
    }, { passive: false });
    
    this.track.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const diffX = startX - endX;
      const diffY = startY - endY;
      
      // Only trigger swipe if horizontal movement is greater than vertical
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        if (diffX > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
        this.resetAutoplay();
      }
      
      isDragging = false;
    }, { passive: true });
  }
  
  startAutoplay() {
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.settings.autoplaySpeed);
  }
  
  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  resetAutoplay() {
    if (this.settings.enableAutoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }
  
  prevSlide() {
    if (this.isTabletSlider) {
      this.currentPage = this.currentPage > 0 ? this.currentPage - 1 : this.totalPages - 1;
    } else {
      this.currentPage = this.currentPage > 0 ? this.currentPage - 1 : this.totalSlides - 1;
    }
    this.updateSlider();
  }
  
  nextSlide() {
    if (this.isTabletSlider) {
      this.currentPage = this.currentPage < this.totalPages - 1 ? this.currentPage + 1 : 0;
    } else {
      this.currentPage = this.currentPage < this.totalSlides - 1 ? this.currentPage + 1 : 0;
    }
    this.updateSlider();
  }
  
  goToSlide(index) {
    this.currentPage = index;
    this.updateSlider();
  }
  
  updateSlider() {
    if (this.isTabletSlider) {
      // Para slider de tablets con múltiples items por página
      const slideWidth = 100 / this.itemsPerPage;
      const offset = this.currentPage * slideWidth * this.itemsPerPage;
      this.track.style.transform = `translateX(-${offset}%)`;
      
      // Update pagination dots
      this.paginationDots.forEach((dot, index) => {
        dot.classList.toggle('hdt-slider__pagination-dot--active', index === this.currentPage);
      });
      
      // Para tablets, NO ocultamos slides - todos deben ser visibles
      // Solo actualizamos aria-hidden para accesibilidad
      this.slides.forEach((slide, index) => {
        const pageStart = this.currentPage * this.itemsPerPage;
        const pageEnd = pageStart + this.itemsPerPage;
        const isInCurrentPage = index >= pageStart && index < pageEnd;
        
        slide.setAttribute('aria-hidden', !isInCurrentPage);
        // NO modificamos visibility ni opacity aquí, lo controla el CSS
        
        if (isInCurrentPage) {
          slide.setAttribute('tabindex', '0');
          const img = slide.querySelector('img');
          if (img && !img.complete) {
            img.loading = 'eager';
          }
        } else {
          slide.setAttribute('tabindex', '-1');
        }
      });
    } else {
      // Para slider normal (mobile) - un item por vez
      const slideWidth = 100;
      const offset = this.currentPage * slideWidth;
      this.track.style.transform = `translateX(-${offset}%)`;
      
      // Update pagination
      this.paginationDots.forEach((dot, index) => {
        dot.classList.toggle('hdt-slider__pagination-dot--active', index === this.currentPage);
      });
      
      // Update slide visibility for accessibility and ensure content is visible
      this.slides.forEach((slide, index) => {
        const isActive = index === this.currentPage;
        slide.setAttribute('aria-hidden', !isActive);
        slide.style.visibility = isActive ? 'visible' : 'hidden';
        slide.style.opacity = isActive ? '1' : '0';
        
        if (isActive) {
          slide.setAttribute('tabindex', '0');
          // Ensure images are loaded
          const img = slide.querySelector('img');
          if (img && !img.complete) {
            img.loading = 'eager';
          }
        } else {
          slide.setAttribute('tabindex', '-1');
        }
      });
    }
  }
  
  hideNavigation() {
    if (this.buttonsContainer) this.buttonsContainer.style.display = 'none';
    if (this.paginationContainer) this.paginationContainer.style.display = 'none';
  }
  
  destroy() {
    this.stopAutoplay();
    window.removeEventListener('resize', () => this.applySettings());
  }
}

// Initialize sliders when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('[data-slider]');
  sliders.forEach(slider => {
    new CollectionsEnhancedSlider(slider);
  });
});

// Re-initialize sliders when sections are loaded dynamically
document.addEventListener('shopify:section:load', (event) => {
  const sliders = event.target.querySelectorAll('[data-slider]');
  sliders.forEach(slider => {
    new CollectionsEnhancedSlider(slider);
  });
});
